import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Bell, CheckCheck, PartyPopper, UserPlus, BellRing, ClipboardList, X, Trash2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { pushSupported, isPushEnabled, enablePush, getRegistration, playAlertChime } from "@/lib/push";

function fmt(iso) {
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
}

export default function NotificationBell({ onAssign }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const seenIds = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      const currentIds = new Set(data.map((n) => n.id));
      if (seenIds.current !== null) {
        const fresh = data.find((n) => !seenIds.current.has(n.id) && !n.read);
        if (fresh) {
          playAlertChime();
          toast.success(fresh.message, { icon: fresh.type === "assignment_request" ? "🙋" : "🎉", duration: 6000 });
          if ("Notification" in window && Notification.permission === "granted") {
            try { new Notification("Paige's Bistro", { body: fresh.message }); }
            catch (err) { console.warn("Browser notification failed", err); }
          }
        }
      }
      seenIds.current = currentIds;
      setItems(data);
    } catch (e) { console.warn("Failed to load notifications", e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getRegistration();
    (async () => { setPushOn(await isPushEnabled()); })();
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const unread = items.filter((n) => !n.read).length;
  const readCount = items.filter((n) => n.read).length;

  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  const dismissOne = async (id) => {
    setItems((prev) => prev.filter((n) => n.id !== id));
    try { await api.delete(`/notifications/${id}`); }
    catch (e) { console.warn("Dismiss failed", e); toast.error("Could not dismiss — please try again"); }
    load();
  };
  const clearRead = async () => {
    try { const { data } = await api.delete("/notifications"); toast.success(`Cleared ${data.deleted || 0} read notification(s)`); }
    catch (e) { toast.error("Could not clear notifications"); }
    load();
  };

  const enable = async () => {
    setPushBusy(true);
    try { await enablePush(); setPushOn(true); toast.success("Phone alerts enabled — you'll be notified even when the app is closed."); }
    catch (e) { toast.error(e.message || "Could not enable alerts"); }
    finally { setPushBusy(false); }
  };

  const handleAssignClick = (n) => {
    if (n.type === "assignment_request" && n.requested_by && onAssign) {
      setOpen(false);
      onAssign(n.requested_by);
    }
  };

  return (
    <div className="relative">
      <button
        data-testid="notification-bell-button"
        onClick={() => setOpen((o) => !o)}
        className="relative h-11 w-11 rounded-xl border border-[#2E2E2E] bg-[#171717] flex items-center justify-center hover:border-[#E11D2A]/50 transition-colors"
      >
        <Bell className="h-5 w-5 text-[#F5F5F5]" />
        {unread > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 rounded-full bg-[#E74C3C] text-white text-[11px] font-bold flex items-center justify-center">
            {unread}
          </span>
        )}
      </button>

      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div data-testid="notification-feed-drawer" className="fixed top-16 right-4 w-[340px] max-w-[90vw] z-[70] rounded-2xl border border-[#2E2E2E] bg-[#171717] shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#2E2E2E]">
              <span className="font-heading font-semibold">Notifications</span>
              <div className="flex items-center gap-3">
                {unread > 0 && (
                  <button data-testid="mark-all-read-button" onClick={markAll} className="flex items-center gap-1 text-xs text-[#A3A3A3] hover:text-[#E11D2A]">
                    <CheckCheck className="h-3.5 w-3.5" /> Mark read
                  </button>
                )}
                {readCount > 0 && (
                  <button data-testid="clear-read-button" onClick={clearRead} className="flex items-center gap-1 text-xs text-[#A3A3A3] hover:text-[#E11D2A]">
                    <Trash2 className="h-3.5 w-3.5" /> Clear read
                  </button>
                )}
              </div>
            </div>

            {pushSupported() && !pushOn && (
              <button data-testid="enable-push-button" onClick={enable} disabled={pushBusy}
                className="w-full flex items-center gap-2.5 px-4 py-3 border-b border-[#242424] bg-[#E11D2A]/[0.07] hover:bg-[#E11D2A]/[0.12] transition-colors text-left">
                <BellRing className="h-4 w-4 text-[#E11D2A] shrink-0" />
                <span className="text-sm text-[#F5F5F5]">{pushBusy ? "Enabling…" : "Turn on phone alerts & sound"}</span>
              </button>
            )}

            <div className="max-h-[60vh] overflow-y-auto">
              {items.length === 0 ? (
                <p className="p-6 text-center text-sm text-[#6B6B6B]">No notifications yet. You'll be alerted when a closing is completed or an employee requests an assignment.</p>
              ) : items.map((n) => {
                const isReq = n.type === "assignment_request";
                return (
                <div
                  key={n.id}
                  data-testid={isReq ? "notification-assignment-request" : n.type === "assignment_added" ? "notification-assignment-added" : "notification-completion-alert"}
                  onClick={() => handleAssignClick(n)}
                  className={`px-4 py-3 border-b border-[#242424] ${!n.read ? "bg-[#E11D2A]/[0.06]" : ""} ${isReq && onAssign ? "cursor-pointer hover:bg-[#232323]" : ""}`}
                >
                  <div className="flex gap-3">
                    {isReq
                      ? <UserPlus className="h-4 w-4 text-[#E11D2A] shrink-0 mt-0.5" />
                      : n.type === "assignment_added"
                      ? <ClipboardList className="h-4 w-4 text-[#E11D2A] shrink-0 mt-0.5" />
                      : n.type === "temp_alert"
                      ? <AlertTriangle className="h-4 w-4 text-[#EF4444] shrink-0 mt-0.5" />
                      : <PartyPopper className="h-4 w-4 text-[#16A34A] shrink-0 mt-0.5" />}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-[#F5F5F5] leading-snug">{n.message}</p>
                      {n.override_note && <p className="text-xs text-[#A3A3A3] mt-1 italic">“{n.override_note}”</p>}
                      {n.handoff_note && <p data-testid="notification-handoff-note" className="text-xs text-[#E5A100] mt-1 italic">Handoff: “{n.handoff_note}”</p>}
                      <div className="flex items-center gap-2 mt-1">
                        <p className="font-mono text-[10px] text-[#6B6B6B]">{fmt(n.created_at)}</p>
                        {isReq && onAssign && <span className="text-[10px] font-semibold text-[#E11D2A]">Tap to assign →</span>}
                      </div>
                    </div>
                    <button
                      data-testid={`dismiss-notification-${n.id}`}
                      onClick={(e) => { e.stopPropagation(); dismissOne(n.id); }}
                      title="Dismiss"
                      className="shrink-0 h-7 w-7 -mt-1 -mr-1 rounded-lg flex items-center justify-center text-[#6B6B6B] hover:text-[#E11D2A] hover:bg-[#E11D2A]/10 transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
